import { useEffect, useState } from "react";
import { KbAPI } from "../../api/kb";
import {
  DepartmentsAPI,
  CategoriesAPI,
  SubcategoriesAPI,
} from "../../api/lookups";

import InternalNoteEditor from "../../components/tiptap/InternalNoteEditor";

export default function KbAdminPage() {
  const [articles, setArticles] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState({ type: "doc", content: [] });
  const [audience, setAudience] = useState("end_user");
  const [editingId, setEditingId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [departmentId, setDepartmentId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [editorKey, setEditorKey] = useState(0);

  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const data = await KbAPI.list();
      setArticles(data);
    } catch (err) {
      console.error("Failed to load KB articles", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    DepartmentsAPI.list()
      .then(setDepartments)
      .catch((err) => console.error("Failed to load departments", err));
  }, []);

  useEffect(() => {
    if (!departmentId) {
      setCategories([]);
      setSubcategories([]);
      return;
    }

    CategoriesAPI.list(departmentId)
      .then((data) => {
        setCategories(data);
      })
      .catch((err) => console.error("Failed to load categories", err));
  }, [departmentId]);

    useEffect(() => {
      if (!categoryId) {
        setSubcategories([]);
        return;
      }

      SubcategoriesAPI.list(categoryId)
        .then((data) => {
          setSubcategories(data);
        })
        .catch((err) => console.error("Failed to load subcategories", err));
    }, [categoryId]);

  async function handleSave() {
    const payload = {
      title,
      body,
      audience_type: audience,
      department_id: departmentId === "" ? null : departmentId,
      category_id: categoryId === "" ? null : Number(categoryId),
      subcategory_id: subcategoryId === "" ? null : Number(subcategoryId),
    };

    if (editingId) {
      await KbAPI.update(editingId, payload);
    } else {
      await KbAPI.create(payload);
    }

    setEditingId(null);
    setTitle("");
    setBody({ type: "doc", content: [] });
    setEditorKey((k) => k + 1);
    setAudience("end_user");

    setDepartmentId("");
    setCategoryId("");
    setSubcategoryId("");

    load();
  }

  if (loading) return <p>Loading…</p>;

  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold mb-4">Knowledge Base Admin</h1>

      {/* CREATE FORM */}
      <div className="border p-4 mb-6">
      <h2 className="font-semibold mb-2">
        {editingId ? "Edit Article" : "Create Article"}
      </h2>
        <input
          className="border p-2 w-full mb-2"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <select
          className="border p-2 w-full mb-2"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
        >
          <option value="end_user">End user</option>
          <option value="assignee">Technician</option>
        </select>

                <select
          className="border p-2 w-full mb-2"
          value={departmentId}
          onChange={(e) => {
            setDepartmentId(e.target.value);
            setCategoryId("");
            setSubcategoryId("");
          }}
        >
          <option value="">Select department</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        <select
          className="border p-2 w-full mb-2"
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            setSubcategoryId("");
          }}
          disabled={!departmentId}
        >
          <option value="">Select category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="border p-2 w-full mb-2"
          value={subcategoryId}
          onChange={(e) => setSubcategoryId(e.target.value)}
          disabled={!categoryId}
        >
          <option value="">Select subcategory</option>
          {subcategories.map((sc) => (
            <option key={sc.id} value={sc.id}>
              {sc.name}
            </option>
          ))}
        </select>

        <InternalNoteEditor
            key={editorKey}
            value={body}
            onChange={setBody}
          />

        <button
          className="mt-2 bg-blue-500 text-white px-3 py-1 rounded"
          onClick={handleSave}
        >
          {editingId ? "Save Changes" : "Create"}
        </button>

        {editingId && (
          <button
            className="mt-2 ml-2 bg-gray-300 px-3 py-1 rounded"
            onClick={() => {
              setEditingId(null);
              setTitle("");
              setBody({ type: "doc", content: [] });
              setAudience("end_user");
              setDepartmentId("");
              setCategoryId("");
              setSubcategoryId("");
              setCategories([]);
              setSubcategories([]);
              setEditorKey((k) => k + 1);
            }}
          >
            Cancel edit
          </button>
        )}
      </div>

      {/* LIST */}
      <div>
        <h2 className="font-semibold mb-2">Articles</h2>
        {articles.map((a) => (
          <div
            key={a.id}
            className="border p-2 mb-2 cursor-pointer hover:bg-gray-50"
            onClick={async () => {
              setEditingId(a.id);
              setTitle(a.title || "");
              let nextBody = { type: "doc", content: [] };

              if (typeof a.body === "string") {
                try {
                  nextBody = JSON.parse(a.body);
                } catch {
                  nextBody = { type: "doc", content: [] };
                }
              } else if (a.body) {
                nextBody = a.body;
              }

              setBody(nextBody);
              setEditorKey((k) => k + 1);
              setAudience(a.audience_type || "end_user");

              const nextDepartmentId = a.department_id ? String(a.department_id) : "";
              const nextCategoryId = a.category_id ? String(a.category_id) : "";
              const nextSubcategoryId = a.subcategory_id ? String(a.subcategory_id) : "";

              setDepartmentId(nextDepartmentId);

              if (nextDepartmentId) {
                const loadedCategories = await CategoriesAPI.list(nextDepartmentId);
                setCategories(loadedCategories);
              } else {
                setCategories([]);
              }

              setCategoryId(nextCategoryId);

              if (nextCategoryId) {
                const loadedSubcategories = await SubcategoriesAPI.list(nextCategoryId);
                setSubcategories(loadedSubcategories);
              } else {
                setSubcategories([]);
              }

              setSubcategoryId(nextSubcategoryId);
            }}
          >
            <div className="font-medium">{a.title}</div>
            <div className="text-xs text-gray-500">
              {a.audience_type}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}